import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Radio, RadioGroup, FormControl, FormLabel, Grid } from '@mui/material';
import logo from '../../assets/UpdatedMainLogo.png';
import getApiBase from '../../components/getApiBase';

// Header with consistent styling
const Header = ({ onLogout }) => {
  const navigate = useNavigate();
  
  return (
    <header style={{
      backgroundColor: '#fff',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      padding: '10px 20px',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <img src={logo} alt="Autoshops.com Logo" style={{ height: 'auto', width: '300px' }} />
        </Box>
        <nav style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <a 
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin');
            }}
            style={{ 
              color: '#ea4335', 
              textDecoration: 'none', 
              fontFamily: 'Poppins, sans-serif', 
              fontWeight: 700,
              fontSize: '18px',
              padding: '10px',
              borderRadius: '8px',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e63926';
              e.target.style.color = '#fff';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#ea4335';
            }}
          >
            ← Back to Dashboard
          </a>
          <span style={{ 
            color: '#000', 
            fontFamily: 'Poppins, sans-serif', 
            fontWeight: 700, 
            fontSize: '18px',
            padding: '10px' 
          }}>
            Team Management
          </span>
          <Button
            onClick={onLogout}
            variant="contained"
            sx={{
              backgroundColor: '#4285f4',
              color: 'white',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#1b449e',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Logout
          </Button>
        </nav>
      </Box>
    </header>
  );
};

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviting, setInviting] = useState(false);
  const [myPermissions, setMyPermissions] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRoles, setEditingRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [testAccountModalOpen, setTestAccountModalOpen] = useState(false);
  const [testAccountModalMessage, setTestAccountModalMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyPermissions();
    fetchAdmins();
    fetchRoleTypes();
  }, []);

  const fetchMyPermissions = async () => {
    try {
      const response = await fetch(`${getApiBase()}/api/admin-management/my-permissions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('MyPermissions data:', data);
        setMyPermissions(data);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  };

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiBase()}/api/admin-management/roles`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Admin management API response:', data);
        console.log('📊 Number of admins/users returned:', data.admins?.length || 0);
        setAdmins(data.admins || []);
      } else if (response.status === 403) {
        setError('You do not have permission to manage admins');
      } else {
        setError('Failed to load admin list');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleTypes = async () => {
    try {
      const response = await fetch(`${getApiBase()}/api/admin-management/role-types`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Role types response:', data);
        console.log('Fetched role types:', data.roleTypes);
        setRoleTypes(data.roleTypes || []);
        
        // Set default role to first available role user can invite
        const invitableRoles = (data.roleTypes || []).filter(rt => rt.canInvite);
        if (invitableRoles.length > 0 && !inviteRole) {
          setInviteRole(invitableRoles[0].type);
        }
      } else {
        console.error('Failed to fetch role types:', response.status);
      }
    } catch (err) {
      console.error('Error fetching role types:', err);
    }
  };

  const searchUsers = async (query) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        `${getApiBase()}/api/admin-management/search-users?query=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.users);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchUsers(searchQuery);
      }
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    
    try {
      const requestBody = {
        roleType: inviteRole
      };

      if (selectedUser) {
        requestBody.userId = selectedUser.id;
      } else if (inviteEmail) {
        requestBody.email = inviteEmail;
      } else {
        alert('Please select a user or enter an email');
        setInviting(false);
        return;
      }

      const response = await fetch(`${getApiBase()}/api/admin-management/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        alert('Invitation sent successfully!');
        setShowInviteForm(false);
        setInviteEmail('');
        setSelectedUser(null);
        setSearchQuery('');
        setSearchResults([]);
        // Reset to default role
        const invitableRoles = roleTypes.filter(rt => rt.canInvite);
        if (invitableRoles.length > 0) {
          setInviteRole(invitableRoles[0].type);
        } else {
          setInviteRole('');
        }
        fetchAdmins(); // Refresh the list
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to send invitation');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  // Check if an email is a test account
  const isTestAccount = (email) => {
    if (!email) return false;
    return email.toLowerCase().includes('@test.autoshop.com') || 
           email.toLowerCase().includes('test@') ||
           email.toLowerCase().startsWith('qa.') ||
           email.toLowerCase().startsWith('test.');
  };

  const handleEditRole = (person) => {
    // Check if current user is a test account trying to edit a real account
    // Get current user's email from admins list
    const currentUser = admins.find(a => a.user_id === myPermissions?.userId);
    const currentUserEmail = currentUser?.email || '';
    const targetEmail = person.email || '';
    
    const currentUserIsTest = isTestAccount(currentUserEmail);
    const targetIsTest = isTestAccount(targetEmail);
    
    if (currentUserIsTest && !targetIsTest) {
      // Test account trying to edit real account - show modal
      setTestAccountModalMessage(`Test Account cannot edit a real account.`);
      setTestAccountModalOpen(true);
      return;
    }
    
    // Get current roles for this user
    const currentRoles = person.admin_roles || [];
    
    // Get available roles (roles lower than editor's role)
    const editorPrimaryRole = myPermissions?.primaryRole?.roleType;
    const editorHierarchy = getRoleHierarchy(editorPrimaryRole);
    
    const rolesUserCanAssign = roleTypes.filter(rt => {
      const roleHierarchy = getRoleHierarchy(rt.type);
      return roleHierarchy < editorHierarchy;
    });

    // Initialize editing roles with current roles
    const initialRoles = currentRoles.map(role => ({
      roleType: role.role_type,
      isActive: role.is_active,
      primaryRole: role.primary_role || false,
      existing: true
    }));

    // Add available roles that aren't already assigned
    rolesUserCanAssign.forEach(rt => {
      if (!currentRoles.find(r => r.role_type === rt.type)) {
        initialRoles.push({
          roleType: rt.type,
          isActive: false,
          primaryRole: false,
          existing: false
        });
      }
    });

    setEditingUser(person);
    setEditingRoles(initialRoles);
    setAvailableRoles(rolesUserCanAssign);
    setEditRoleModalOpen(true);
  };

  const handleSaveRoles = async () => {
    if (!editingUser) return;

    // Filter to only active roles
    const activeRoles = editingRoles.filter(r => r.isActive);
    
    if (activeRoles.length === 0) {
      if (!window.confirm('This will remove all admin roles from this user. Continue?')) {
        return;
      }
    }

    // Ensure at least one primary role if there are active roles
    if (activeRoles.length > 0 && !activeRoles.some(r => r.primaryRole)) {
      // Auto-set highest hierarchy role as primary
      const roleHierarchy = {
        'super_admin': 7,
        'admin': 6,
        'developer': 5,
        'moderator': 4,
        'tester': 3,
        'qa': 3,
        'sales': 2,
        'marketing': 2,
        'viewer': 1
      };
      
      const sorted = [...activeRoles].sort((a, b) => {
        const levelA = roleHierarchy[a.roleType] || 0;
        const levelB = roleHierarchy[b.roleType] || 0;
        return levelB - levelA;
      });
      
      if (sorted.length > 0) {
        sorted[0].primaryRole = true;
      }
    }

    try {
      const response = await fetch(`${getApiBase()}/api/admin-management/roles/${editingUser.user_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          roles: editingRoles.map(r => ({
            roleType: r.roleType,
            isActive: r.isActive,
            primaryRole: r.primaryRole
          }))
        })
      });

      if (response.ok) {
        alert('Roles updated successfully!');
        setEditRoleModalOpen(false);
        setEditingUser(null);
        setEditingRoles([]);
        fetchAdmins(); // Refresh the list
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update roles');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    }
  };

  const getRoleBadgeColor = (roleType) => {
    switch (roleType) {
      case 'super_admin': return '#8b5cf6';
      case 'admin': return '#3b82f6';
      case 'developer': return '#10b981';
      case 'moderator': return '#f59e0b';
      case 'tester': return '#ec4899';
      case 'qa': return '#ec4899';
      case 'sales': return '#06b6d4';
      case 'marketing': return '#06b6d4';
      case 'viewer': return '#10b981';
      default: return '#6b7280';
    }
  };

  // Role hierarchy levels (higher number = higher hierarchy)
  const getRoleHierarchy = (roleType) => {
    const hierarchy = {
      'super_admin': 7,
      'admin': 6,
      'developer': 5,
      'moderator': 4,
      'tester': 3,
      'qa': 3,
      'sales': 2,
      'marketing': 2,
      'viewer': 1
    };
    return hierarchy[roleType] || 0;
  };

  // Check if current user can manage target admin (must be higher hierarchy)
  const canManageTargetAdmin = (targetAdmin, targetPrimaryRole = null) => {
    // Check if user has manage_admins permission from ANY role (merged permissions)
    if (!myPermissions?.permissions?.manage_admins) return false;
    
    // Check ALL roles (primary + secondary) - if ANY role can manage, return true
    if (!myPermissions?.roles || myPermissions.roles.length === 0) return false;
    
    // Get highest role level for current user
    const myHighestLevel = Math.max(...myPermissions.roles.map(r => getRoleHierarchy(r.roleType)));
    const myHighestRole = myPermissions.roles.find(r => getRoleHierarchy(r.roleType) === myHighestLevel);
    
    // Get target's highest role level (assuming targetAdmin.role_type is their highest)
    const targetLevel = getRoleHierarchy(targetAdmin.role_type);
    
    // If same role type, apply primary/secondary rules
    if (myHighestLevel === targetLevel) {
      // Rule: Primary role holders can manage secondary role holders of the same type
      if (myHighestRole?.primaryRole === true && targetPrimaryRole === false) {
        // Actor has it as primary, target has it as secondary - allow if has permission
        return myHighestRole?.permissions?.manage_admins === true;
      }
      
      // Rule: Secondary role holders cannot manage anyone with the same role type (primary or secondary)
      if (myHighestRole?.primaryRole === false) {
        return false; // Secondary cannot manage same type
      }
      
      // Both are primary with same type - equal authority, cannot manage
      return false;
    }
    
    // Different role types - follow normal hierarchy rules
    // Check each role - if ANY role has higher hierarchy than target, can manage
    return myPermissions.roles.some(role => {
      // Super admins can manage anyone (except other super admins, handled above)
      if (role.roleType === 'super_admin') return true;
      
      // Must have manage_admins permission for this specific role
      if (!role.permissions?.manage_admins) return false;
      
      const myLevel = getRoleHierarchy(role.roleType);
      return myLevel > targetLevel;
    });
  };

  const formatRoleName = (roleType) => {
    if (!roleType) return 'Unknown Role';
    // Special case: qa -> QA
    if (roleType.toLowerCase() === 'qa') return 'QA';
    return roleType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleLogout = async () => {
    try {
      await fetch(`${getApiBase()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      localStorage.removeItem("user");
      navigate('/login');
    } catch (err) {
      console.error("Logout failed:", err);
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="h5">Loading admin management...</Typography>
      </Box>
    );
  }

  if (error && !myPermissions) {
    return (
      <>
        <Header onLogout={handleLogout} />
        <Box sx={{ padding: '20px', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: '#dc2626' }}>{error}</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onLogout={handleLogout} />
      <Box sx={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <Box>
            <Typography variant="h4" sx={{ margin: 0 }}>Team Management</Typography>
            <Typography sx={{ margin: '5px 0', color: '#6b7280' }}>
              Manage admin roles and permissions
            </Typography>
          </Box>
        
        {myPermissions?.permissions?.invite_admins && (
          <button
            onClick={async () => {
              // Ensure role types are loaded
              if (roleTypes.length === 0) {
                await fetchRoleTypes();
              }
              setShowInviteForm(true);
              setSearchQuery('');
              setSearchResults([]);
              setSelectedUser(null);
              setInviteEmail('');
              // Set default role
              const invitableRoles = roleTypes.filter(rt => rt.canInvite);
              if (invitableRoles.length > 0) {
                setInviteRole(invitableRoles[0].type);
              }
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            + Invite Team Member
          </button>
        )}
      </Box>

      {/* Current User Info and Permissions */}
      {myPermissions && (
        <Box sx={{
          backgroundColor: '#f3f4f6',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <Typography variant="h6" sx={{ margin: '0 0 15px 0' }}>Your Admin Permissions</Typography>
          
          <Box sx={{ marginBottom: '20px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {/* Show primary role only */}
              {myPermissions.primaryRole && (
                <span style={{
                  backgroundColor: getRoleBadgeColor(myPermissions.primaryRole.roleType),
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {formatRoleName(myPermissions.primaryRole.roleType)} ⭐
                </span>
              )}
              <span style={{ color: '#6b7280' }}>
                {myPermissions.primaryRole?.description || myPermissions.description}
              </span>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            {/* What You Can Do - Left Column */}
            <Box sx={{ 
              backgroundColor: 'white', 
              borderRadius: '6px', 
              padding: '15px',
              border: '1px solid #e5e7eb',
              flex: '1',
              minWidth: '300px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>What You Can Do:</h4>
              
              {myPermissions.primaryRole?.roleType === 'super_admin' && (
                <ul style={{ margin: '0', paddingLeft: '20px', color: '#374151' }}>
                  <li>Invite and manage ALL role types (Super Admin, Admin, Moderator, Viewer)</li>
                  <li>Remove any admin except yourself</li>
                  <li>Full access to all system features</li>
                  <li>Manage billing and system settings</li>
                </ul>
              )}
              
              {myPermissions.primaryRole?.roleType === 'admin' && (
                <ul style={{ margin: '0', paddingLeft: '20px', color: '#374151' }}>
                  <li>Invite Moderators and Viewers only</li>
                  <li>Cannot invite other Admins or Super Admins</li>
                  <li>Cannot remove any admins</li>
                  <li>Manage shops and users</li>
                </ul>
              )}
              
              {myPermissions.primaryRole?.roleType === 'moderator' && (
                <ul style={{ margin: '0', paddingLeft: '20px', color: '#374151' }}>
                  <li>Cannot invite new admins</li>
                  <li>Cannot manage other admins</li>
                  <li>Can approve/reject shops</li>
                  <li>Can manage regular users</li>
                </ul>
              )}
              
              {myPermissions.primaryRole?.roleType === 'viewer' && (
                <ul style={{ margin: '0', paddingLeft: '20px', color: '#374151' }}>
                  <li>Read-only access</li>
                  <li>Cannot invite or manage admins</li>
                  <li>Can view analytics and reports</li>
                  <li>Cannot make any changes</li>
                </ul>
              )}
              
              {myPermissions.primaryRole?.roleType === 'developer' && (
                <ul style={{ margin: '0', paddingLeft: '20px', color: '#374151' }}>
                  <li>Technical access for development and testing</li>
                  <li>Can manage shops for testing purposes</li>
                  <li>Can manage users for test data</li>
                  <li>System settings access for dev work</li>
                  <li>Can invite and manage admins for testing</li>
                </ul>
              )}
              
              {(myPermissions.primaryRole?.roleType === 'tester' || myPermissions.primaryRole?.roleType === 'qa') && (
                <ul style={{ margin: '0', paddingLeft: '20px', color: '#374151' }}>
                  <li>QA access for testing shops and workflows</li>
                  <li>Can manage shops for testing purposes</li>
                  <li>Can approve/reject shops for testing workflows</li>
                  <li>Can manage users for test data</li>
                  <li>Can view analytics</li>
                  <li>Cannot invite or manage other admins</li>
                </ul>
              )}
              
              {(myPermissions.primaryRole?.roleType === 'sales' || myPermissions.primaryRole?.roleType === 'marketing') && (
                <ul style={{ margin: '0', paddingLeft: '20px', color: '#374151' }}>
                  <li>Access to view analytics and sales/marketing metrics</li>
                  <li>Can manage users for customer support</li>
                  <li>Read-only access to shops</li>
                  <li>Cannot invite or manage admins</li>
                  <li>Cannot approve shops or change system settings</li>
                </ul>
              )}
            </Box>

            {/* Secondary Roles - Right Column */}
            <Box sx={{ 
              backgroundColor: 'white', 
              borderRadius: '6px', 
              padding: '15px',
              border: '1px solid #e5e7eb',
              flex: '1',
              minWidth: '300px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Secondary Roles:</h4>
              {myPermissions.roles && myPermissions.roles.filter(r => !r.primaryRole).length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {myPermissions.roles
                    .filter(r => !r.primaryRole)
                    .map((role, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          backgroundColor: getRoleBadgeColor(role.roleType),
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {formatRoleName(role.roleType)}
                        </span>
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>
                          {role.description || 'Additional role permissions'}
                        </span>
                      </Box>
                    ))}
                </Box>
              ) : (
                <p style={{ margin: '0', color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}>
                  No secondary roles assigned
                </p>
              )}
            </Box>
          </Box>

          <Box sx={{ 
            marginTop: '15px',
            padding: '12px',
            backgroundColor: '#fef3c7',
            borderRadius: '6px',
            border: '1px solid #fde68a'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
              <strong>📋 Hierarchy Rule:</strong> You can only manage (edit/remove) admins with roles below your own level. 
              Role hierarchy (highest to lowest): Super Admin → Admin → Developer → Moderator → Tester/QA → Sales/Marketing → Viewer.
              You cannot edit or remove admins with equal or higher hierarchy than your own.
            </p>
          </Box>
        </Box>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Box sx={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <Typography variant="h5" sx={{ marginTop: 0 }}>Invite New Team Member</Typography>
            
            {/* Role Hierarchy Visual */}
            <Box sx={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <Typography variant="h6" sx={{ margin: '0 0 10px 0', fontSize: '16px' }}>Role Hierarchy</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    backgroundColor: getRoleBadgeColor('super_admin'),
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    minWidth: '90px',
                    textAlign: 'center'
                  }}>
                    Super Admin
                  </span>
                  <span style={{ color: '#6b7280' }}>→ Can invite all roles</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    backgroundColor: getRoleBadgeColor('admin'),
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    minWidth: '90px',
                    textAlign: 'center'
                  }}>
                    Admin
                  </span>
                  <span style={{ color: '#6b7280' }}>→ Can invite Moderator & Viewer</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    backgroundColor: getRoleBadgeColor('moderator'),
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    minWidth: '90px',
                    textAlign: 'center'
                  }}>
                    Moderator
                  </span>
                  <span style={{ color: '#6b7280' }}>→ Cannot invite</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    backgroundColor: getRoleBadgeColor('viewer'),
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    minWidth: '90px',
                    textAlign: 'center'
                  }}>
                    Viewer
                  </span>
                  <span style={{ color: '#6b7280' }}>→ Cannot invite</span>
                </Box>
              </Box>
            </Box>
            
            <form onSubmit={handleInvite}>
              <Box sx={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Search User by Name
                </label>
                <Box sx={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedUser(null);
                      setInviteEmail('');
                    }}
                    placeholder="Start typing name..."
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                  
                  {/* Search Results Dropdown */}
                  {searchQuery.length >= 2 && !selectedUser && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderTop: 'none',
                      borderRadius: '0 0 6px 6px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 10,
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                      {searchResults.length > 0 ? searchResults.map(user => (
                        <div
                          key={user.id}
                          onClick={() => {
                            setSelectedUser(user);
                            setInviteEmail(user.email);
                            setSearchQuery(`${user.first_name} ${user.last_name}`);
                            setSearchResults([]);
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #e5e7eb',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          <div style={{ fontWeight: '500' }}>
                            {user.first_name} {user.last_name}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            {user.email}
                          </div>
                        </div>
                      )) : (
                        <div style={{
                          padding: '15px',
                          textAlign: 'center',
                          color: '#6b7280'
                        }}>
                          {searching ? 'Searching...' : 'No users found (already admin or no match)'}
                        </div>
                      )}
                    </div>
                  )}
                </Box>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                margin: '15px 0',
                color: '#9ca3af'
              }}>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }}></Box>
                <span style={{ padding: '0 10px', fontSize: '14px' }}>OR</span>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }}></Box>
              </Box>

              <Box sx={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    setSelectedUser(null);
                    setSearchQuery('');
                  }}
                  required
                  placeholder="admin@example.com"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px',
                    backgroundColor: selectedUser ? '#f3f4f6' : 'white'
                  }}
                  readOnly={!!selectedUser}
                />
                {selectedUser && (
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#059669' }}>
                    ✓ User selected: {selectedUser.first_name} {selectedUser.last_name}
                  </p>
                )}
              </Box>

              <Box sx={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Role Type
                </label>
                {roleTypes.length === 0 ? (
                  <Box sx={{
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#6b7280',
                    textAlign: 'center'
                  }}>
                    Loading roles...
                  </Box>
                ) : (
                  <>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '16px',
                        cursor: 'pointer'
                      }}
                    >
                      {!inviteRole && (
                        <option value="">Select a role...</option>
                      )}
                      {roleTypes.filter(rt => rt.canInvite).map(roleType => (
                        <option key={roleType.type} value={roleType.type}>
                          {roleType.name} - {roleType.description}
                        </option>
                      ))}
                    </select>
                    {roleTypes.filter(rt => rt.canInvite).length === 0 && (
                      <p style={{ 
                        margin: '5px 0 0 0', 
                        fontSize: '14px', 
                        color: '#dc2626' 
                      }}>
                        You don't have permission to invite any role types.
                      </p>
                    )}
                  </>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  onClick={() => {
                    setShowInviteForm(false);
                    setSearchQuery('');
                    setSearchResults([]);
                    setSelectedUser(null);
                    setInviteEmail('');
                  }}
                  disabled={inviting}
                  variant="contained"
                  sx={{
                    padding: '10px 20px',
                    backgroundColor: '#6b7280',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#4b5563'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={inviting}
                  variant="contained"
                  sx={{
                    padding: '10px 20px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#2563eb'
                    }
                  }}
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      )}

      {/* Admin List */}
      <Box sx={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Email</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Primary Role</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((person, idx) => {
              const hasAdminRoles = person.admin_roles && person.admin_roles.length > 0;
              const primaryAdminRole = person.primary_admin_role;
              
              // Find target's highest role (for primary/secondary comparison)
              const targetActiveRoles = (person.admin_roles || []).filter(r => r.is_active);
              const targetHighestLevel = targetActiveRoles.length > 0 
                ? Math.max(...targetActiveRoles.map(r => getRoleHierarchy(r.role_type)))
                : 0;
              const targetHighestRole = targetActiveRoles.find(r => getRoleHierarchy(r.role_type) === targetHighestLevel);
              
              return (
                <tr key={person.user_id || idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>
                    {person.first_name} {person.last_name}
                  </td>
                  <td style={{ padding: '12px' }}>{person.email}</td>
                  <td style={{ padding: '12px' }}>
                    {hasAdminRoles ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {/* Only show primary role in the table */}
                        {person.admin_roles
                          .filter(adminRole => adminRole.primary_role && adminRole.is_active)
                          .map((adminRole, roleIdx) => (
                            <span
                              key={roleIdx}
                              style={{
                                backgroundColor: getRoleBadgeColor(adminRole.role_type),
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '500',
                                opacity: adminRole.is_active ? 1 : 0.6
                              }}
                              title="Primary Role"
                            >
                              {formatRoleName(adminRole.role_type)} ⭐
                            </span>
                          ))}
                        {/* Fallback: if no primary role is marked, show the first active role */}
                        {person.admin_roles.filter(adminRole => adminRole.primary_role && adminRole.is_active).length === 0 && (
                          <span
                            style={{
                              backgroundColor: getRoleBadgeColor(person.admin_roles.find(r => r.is_active)?.role_type || 'viewer'),
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            title="Primary Role (auto-selected)"
                          >
                            {formatRoleName(person.admin_roles.find(r => r.is_active)?.role_type || 'viewer')} ⭐
                          </span>
                        )}
                      </Box>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '12px', fontStyle: 'italic' }}>
                        No admin roles
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {hasAdminRoles ? (
                      <span style={{
                        color: person.admin_roles.some((r) => r.is_active) ? '#10b981' : '#dc2626',
                        fontWeight: '500',
                        fontSize: '14px'
                      }}>
                        {person.admin_roles.some((r) => r.is_active) ? 'Active' : 'Inactive'}
                      </span>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '14px' }}>N/A</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {person.user_id === myPermissions?.userId ? (
                      <span style={{ color: '#6b7280', fontSize: '14px' }}>You</span>
                    ) : (
                      // Only show actions if:
                      // 1. User has manage_admins permission
                      // 2. Target has admin roles
                      // 3. Target's primary admin role can be managed (lower hierarchy)
                      myPermissions?.permissions?.manage_admins && 
                      hasAdminRoles && 
                      targetHighestRole && 
                      canManageTargetAdmin(
                        { role_type: targetHighestRole.role_type }, 
                        targetHighestRole.primary_role
                      ) ? (
                        <button
                          onClick={() => handleEditRole(person)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Edit Role
                        </button>
                      ) : hasAdminRoles && primaryAdminRole ? (
                        // Show "Cannot manage" only if they have admin roles but can't be managed
                        <span 
                          style={{ 
                            color: '#9ca3af', 
                            fontSize: '12px',
                            fontStyle: 'italic'
                          }}
                          title={`Cannot manage ${formatRoleName(primaryAdminRole.role_type)} - equal or higher hierarchy`}
                        >
                          Cannot manage
                        </span>
                      ) : null
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {/* Edit Role Modal */}
      <Dialog 
        open={editRoleModalOpen} 
        onClose={() => {
          setEditRoleModalOpen(false);
          setEditingUser(null);
          setEditingRoles([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#3b82f6', color: 'white', fontWeight: 600 }}>
          Edit Roles: {editingUser ? `${editingUser.first_name} ${editingUser.last_name}` : ''}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editingUser && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
                Email: {editingUser.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#6b7280', fontSize: '0.875rem' }}>
                You can add, remove, or modify roles. Only roles lower than your own can be assigned.
              </Typography>

              <Grid container spacing={3}>
                {/* Left Column: Primary Role */}
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                      Primary Role ⭐
                    </FormLabel>
                    <RadioGroup
                      value={editingRoles.find(r => r.primaryRole && r.isActive)?.roleType || ''}
                      onChange={(e) => {
                        setEditingRoles(editingRoles.map(r => ({
                          ...r,
                          primaryRole: r.roleType === e.target.value && r.isActive
                        })));
                      }}
                    >
                      {editingRoles.filter(r => r.isActive).length === 0 && (
                        <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic', p: 2 }}>
                          No active roles. Select at least one role in "Available Roles" to set as primary.
                        </Typography>
                      )}
                      {editingRoles
                        .filter(r => r.isActive)
                        .map(role => (
                          <Box key={role.roleType} sx={{ mb: 1, p: 1.5, border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                            <FormControlLabel
                              control={
                                <Radio 
                                  value={role.roleType}
                                  disabled={!role.isActive}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span
                                    style={{
                                      backgroundColor: getRoleBadgeColor(role.roleType),
                                      color: 'white',
                                      padding: '4px 12px',
                                      borderRadius: '20px',
                                      fontSize: '12px',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {formatRoleName(role.roleType)}
                                  </span>
                                </Box>
                              }
                            />
                          </Box>
                        ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Right Column: Secondary Roles */}
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                      Secondary Roles
                    </FormLabel>
                    <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {editingRoles
                        .filter(r => r.isActive && !r.primaryRole)
                        .map(role => (
                          <Box key={role.roleType} sx={{ mb: 1, p: 1.5, border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={true}
                                  disabled={true}
                                  sx={{ opacity: 0.6 }}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span
                                    style={{
                                      backgroundColor: getRoleBadgeColor(role.roleType),
                                      color: 'white',
                                      padding: '4px 12px',
                                      borderRadius: '20px',
                                      fontSize: '12px',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {formatRoleName(role.roleType)}
                                  </span>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      // Remove this secondary role
                                      const newRoles = editingRoles.map(r => 
                                        r.roleType === role.roleType 
                                          ? { ...r, isActive: false, primaryRole: false }
                                          : r
                                      );
                                      setEditingRoles(newRoles);
                                    }}
                                    sx={{ 
                                      minWidth: 'auto', 
                                      padding: '2px 8px',
                                      fontSize: '11px',
                                      color: '#dc2626',
                                      '&:hover': { backgroundColor: '#fee2e2' }
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              }
                            />
                          </Box>
                        ))}
                      {editingRoles.filter(r => r.isActive && !r.primaryRole).length === 0 && (
                        <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic', p: 2 }}>
                          No secondary roles. Select additional roles below to add as secondary.
                        </Typography>
                      )}
                    </Box>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Available Roles Section - Full Width Below */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600, display: 'block' }}>
                  Available Roles
                </FormLabel>
                <Typography variant="body2" sx={{ mb: 2, color: '#6b7280', fontSize: '0.875rem' }}>
                  Check a role to add it. If not selected as primary, it will be added as a secondary role.
                </Typography>
                {editingRoles.map(role => (
                  <Box key={role.roleType} sx={{ mb: 1.5, p: 1.5, border: '1px solid #e5e7eb', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={role.isActive}
                          onChange={(e) => {
                            const wasPrimary = role.primaryRole && role.isActive;
                            const newRoles = editingRoles.map(r => 
                              r.roleType === role.roleType 
                                ? { ...r, isActive: e.target.checked, primaryRole: e.target.checked && r.primaryRole }
                                : r
                            );

                            // If we're unchecking the primary role, auto-select a new primary
                            if (wasPrimary && !e.target.checked) {
                              const activeRoles = newRoles.filter(r => r.isActive && r.roleType !== role.roleType);
                              if (activeRoles.length > 0) {
                                const roleHierarchy = {
                                  'super_admin': 7,
                                  'admin': 6,
                                  'developer': 5,
                                  'moderator': 4,
                                  'tester': 3,
                                  'qa': 3,
                                  'sales': 2,
                                  'marketing': 2,
                                  'viewer': 1
                                };
                                
                                const sorted = [...activeRoles].sort((a, b) => {
                                  const levelA = roleHierarchy[a.roleType] || 0;
                                  const levelB = roleHierarchy[b.roleType] || 0;
                                  return levelB - levelA;
                                });
                                
                                sorted[0].primaryRole = true;
                              }
                            }

                            setEditingRoles(newRoles);
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span
                            style={{
                              backgroundColor: getRoleBadgeColor(role.roleType),
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '500',
                              opacity: role.isActive ? 1 : 0.5
                            }}
                          >
                            {formatRoleName(role.roleType)}
                          </span>
                          {role.isActive && role.primaryRole && (
                            <span style={{ color: '#10b981', fontSize: '11px', fontWeight: 500 }}>⭐ Primary</span>
                          )}
                          {role.isActive && !role.primaryRole && (
                            <span style={{ color: '#6b7280', fontSize: '11px' }}>Secondary</span>
                          )}
                          {role.existing && !role.isActive && (
                            <span style={{ color: '#dc2626', fontSize: '11px' }}>(Will be removed)</span>
                          )}
                        </Box>
                      }
                    />
                    {!role.isActive && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          // Add as secondary role
                          const newRoles = editingRoles.map(r => 
                            r.roleType === role.roleType 
                              ? { ...r, isActive: true, primaryRole: false }
                              : r
                          );
                          setEditingRoles(newRoles);
                        }}
                        sx={{ 
                          minWidth: 'auto', 
                          padding: '4px 12px',
                          fontSize: '11px',
                          borderColor: '#3b82f6',
                          color: '#3b82f6',
                          '&:hover': { 
                            borderColor: '#2563eb',
                            backgroundColor: '#eff6ff'
                          }
                        }}
                      >
                        Add as Secondary
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>

              {editingRoles.filter(r => r.isActive).length === 0 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#fef3c7', borderRadius: '4px' }}>
                  <Typography variant="body2" sx={{ color: '#92400e' }}>
                    ⚠️ Warning: Removing all roles will revoke admin access for this user.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setEditRoleModalOpen(false);
              setEditingUser(null);
              setEditingRoles([]);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRoles}
            variant="contained"
            sx={{ backgroundColor: '#3b82f6' }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>

      {/* Test Account Restriction Modal */}
      <Dialog 
        open={testAccountModalOpen} 
        onClose={() => setTestAccountModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#dc2626', color: 'white', fontWeight: 600 }}>
          ⚠️ Test Account Restriction
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {testAccountModalMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Test accounts are restricted from editing real user accounts to prevent accidental changes to production data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setTestAccountModalOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: '#6b7280',
              color: 'white',
              '&:hover': {
                backgroundColor: '#4b5563'
              }
            }}
          >
            Understood
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminManagement;